import { CircularProgress, FormControl } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FoodsSelectServerPagination } from "@foodpilot/foods";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useIngredientSelector } from "src/api/client-api/ingredients/useIngredientSelector";
import type { SelectorIngredient } from "src/api/client-api/ingredients/ingredientSchema";
import { useGetOneIngredient } from "src/api/client-api/ingredients/useGetOneIngredient";

interface FormIngredientSelectorProps {
    value: string;
    onChange: (value: string) => void;
}

export const FormIngredientSelector = ({ value, onChange }: FormIngredientSelectorProps) => {
    const { t } = useTranslation();
    const { search, delayedSearch, setSearch } = useDelayedSearch();

    const { ingredients, isLoading } = useIngredientSelector({
        searchText: delayedSearch,
        enabled: true,
    });

    const selectedListIngredient = ingredients.find((i) => i.id.toString() === value);
    const { ingredient: selectedIngredient, isLoading: isIngredientLoading } = useGetOneIngredient(value);

    if (!selectedListIngredient && value && isIngredientLoading) return <CircularProgress size={40} />;

    const isSearchFound = (searchValue: string) => {
        if (search === "") return true;
        return searchValue.toLowerCase().includes(search.toLowerCase());
    };

    return (
        <FormControl fullWidth>
            <FoodsSelectServerPagination
                selectedOption={(selectedListIngredient ?? selectedIngredient) || null}
                options={isLoading ? [] : ingredients}
                onChange={(ingredient) => onChange(ingredient?.id.toString() || "")}
                getId={(ingredient: SelectorIngredient) => ingredient.id}
                getName={(ingredient: SelectorIngredient) =>
                    `${ingredient.name}${ingredient.ingredientLine ? ` - ${ingredient.ingredientLine.name}` : ""}`
                }
                textOptions={{
                    placeholder: t("ingredient.select.placeholder", "Select an ingredient"),
                    searchPlaceholder: t("ingredient.select.searchPlaceholder", "Search ingredients..."),
                    emptyOptionsText: t("ingredient.select.noOptions", "No ingredients available"),
                    defaultValueText: t("ingredient.select.defaultValue", "Default value"),
                    noResult:
                        isLoading ?
                            t("ingredient.select.loading", "Loading...")
                        :   t("ingredient.select.noResultFound", "No ingredients found"),
                }}
                search={search}
                setSearch={setSearch}
                isSearchFound={isSearchFound}
            />
        </FormControl>
    );
};
